"use client";

import { Loader2, Search } from "lucide-react";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { useNavbarSearch } from "@/hooks/use-navbar-search";
import { SearchDropdown } from "./search-dropdown";

/**
 * Navbar search input component with dropdown functionality.
 * Used specifically in the navbar (desktop and mobile).
 * Provides real-time search with debouncing, loading feedback, and product suggestions dropdown.
 * Fixed for SSR hydration compatibility and navigation cleanup.
 */
export function NavbarSearchInput() {
  const { search, setSearch, isSearching, debouncedSearch, clearSearch } =
    useNavbarSearch();
  const [isMounted, setIsMounted] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const pathname = usePathname();
  const inputRef = useRef<HTMLInputElement>(null);

  // Prevent hydration mismatch by only showing dynamic content after mount
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Clear search and close dropdown when navigating to a different page
  useEffect(() => {
    // Read pathname so this effect actually depends on it (prevents exhaustive-deps warning).
    // This ensures the effect runs whenever the route changes.
    void pathname;

    setIsDropdownOpen(false);
    clearSearch();
  }, [pathname, clearSearch]);

  // Handle input focus to open dropdown
  const handleFocus = () => {
    if (debouncedSearch.length >= 2) {
      setIsDropdownOpen(true);
    }
  };

  // Handle input change to open dropdown when typing
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);
    if (value.length >= 2) {
      setIsDropdownOpen(true);
    } else {
      setIsDropdownOpen(false);
    }
  };

  // Handle dropdown close and clear search
  const handleDropdownClose = () => {
    setIsDropdownOpen(false);
    clearSearch();
  };

  return (
    <SearchDropdown
      debouncedSearch={debouncedSearch}
      isOpen={isDropdownOpen}
      onClose={handleDropdownClose}
      setIsOpen={setIsDropdownOpen}
    >
      <div className="relative">
        <Search className="-translate-y-1/2 absolute top-1/2 left-3 h-4 w-4 text-muted-foreground" />
        <Input
          aria-label="Search products"
          autoComplete="off"
          className="pr-10 pl-10"
          onChange={handleInputChange}
          onFocus={handleFocus}
          placeholder="Search products..."
          ref={inputRef}
          suppressHydrationWarning
          value={isMounted ? search : ""}
        />
        {isMounted && isSearching && (
          <Loader2
            aria-label="Searching..."
            className="-translate-y-1/2 absolute top-1/2 right-3 h-4 w-4 animate-spin text-muted-foreground"
          />
        )}
      </div>
    </SearchDropdown>
  );
}
